import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const currencySymbols = {
  EUR: '€',
  USD: '$',
  AED: 'د.إ',
  GBP: '£',
  CHF: 'Fr.',
};

const getBudgetOptions = (currency) => {
  const symbol = currencySymbols[currency] || '€';
  return [
    { value: '1500-2500', label: `${symbol}1.500 - ${symbol}2.500` },
    { value: '2500-5000', label: `${symbol}2.500 - ${symbol}5.000` },
    { value: '5000-10000', label: `${symbol}5.000 - ${symbol}10.000` },
    { value: '10000-25000', label: `${symbol}10.000 - ${symbol}25.000` },
    { value: '25000-50000', label: `${symbol}25.000 - ${symbol}50.000` },
    { value: 'over-50000', label: `${symbol}50.000+` },
  ];
};

export default function CTASection({ content, settings = {} }) {
  const headline = content?.contact_headline || 'Bereit für klare Richtung?';
  const text = content?.contact_description || 'Erzähl uns von deinem Projekt. Wir melden uns innerhalb von 24 Stunden.';
  const contactEmail = content?.contact_email || 'office@someones-direction.at';
  const contactPhone = content?.contact_phone || '+43 681 804564';
  const contactLocation = content?.contact_location || 'Wien, Österreich';
  const whatsappLink = settings?.whatsapp_link || '';
  const budgetCurrency = settings?.budget_currency || 'EUR';
  const turnstileSiteKey = settings?.turnstile_site_key || '';
  
  const budgetOptions = useMemo(() => getBudgetOptions(budgetCurrency), [budgetCurrency]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
    preferred_contact: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Load Turnstile script dynamically
  useEffect(() => {
    if (!turnstileSiteKey) return;

    // Check if script already loaded
    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => {
      renderTurnstile();
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup widget on unmount
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (e) {
          // Widget may already be removed
        }
      }
    };
  }, [turnstileSiteKey]);

  const renderTurnstile = () => {
    if (!turnstileRef.current || !window.turnstile || !turnstileSiteKey) return;
    
    // Remove existing widget if any
    if (turnstileWidgetId.current) {
      try {
        window.turnstile.remove(turnstileWidgetId.current);
      } catch (e) {
        // Widget may already be removed
      }
    }

    try {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'light',
        callback: (token) => {
          setTurnstileToken(token);
        },
        'expired-callback': () => {
          setTurnstileToken('');
        },
        'error-callback': () => {
          setTurnstileToken('');
        }
      });
    } catch (e) {
      console.error('Turnstile render error:', e);
    }
  };

  const resetTurnstile = () => {
    if (turnstileWidgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (e) {
        // Widget may not exist
      }
    }
    setTurnstileToken('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check Turnstile if configured
    if (turnstileSiteKey && !turnstileToken) {
      toast.error('Bitte die Sicherheitsprüfung abschließen');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          turnstile_token: turnstileToken || null
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          toast.success('Nachricht gesendet! Wir melden uns in Kürze.');
        } else {
          toast.success('Nachricht erhalten! Wir melden uns in Kürze.');
        }
        setFormData({ name: '', email: '', phone: '', company: '', budget: '', preferred_contact: '', message: '' });
        resetTurnstile();
      } else {
        toast.error('Senden fehlgeschlagen. Bitte erneut versuchen.');
        resetTurnstile();
      }
    } catch (error) {
      toast.error('Senden fehlgeschlagen. Bitte erneut versuchen.');
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-padding bg-background relative overflow-hidden" id="contact">
      {/* Dezente Bildmarke im Hintergrund */}
      <img
        src="/logo-mark-red.png"
        alt=""
        aria-hidden="true"
        className="absolute -left-24 top-16 w-[380px] opacity-[0.05] pointer-events-none select-none"
      />

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Text */}
          <motion.div
            initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="kicker mb-6 inline-flex">Kontakt</span>
            <h2 className="font-display text-5xl sm:text-6xl text-foreground mb-6 leading-[0.95]">
              {headline}
            </h2>
            <p className="text-lg text-foreground/80 mb-10 max-w-xl">
              {text}
            </p>

            {/* WhatsApp Button */}
            {whatsappLink && (
              <motion.a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-background border border-foreground/30 text-foreground text-sm font-medium rounded-sm hover:border-foreground transition-colors mb-10"
              >
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                <span>WhatsApp</span>
              </motion.a>
            )}

            {/* Contact details */}
            <div className="space-y-6 pt-8 border-t border-border/30">
              <div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  E-Mail
                </span>
                <a 
                  href={`mailto:${contactEmail}`}
                  className="text-foreground hover:text-muted-foreground transition-colors"
                >
                  {contactEmail}
                </a>
              </div>
              {whatsappLink && (
              <div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Telefon
                </span>
                <a 
                  href={`tel:${contactPhone.replace(/\s/g, '')}`}
                  className="text-foreground hover:text-muted-foreground transition-colors"
                >
                  {contactPhone}
                </a>
              </div>
              )}
              <div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Standort
                </span>
                <p className="text-foreground">{contactLocation}</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 40, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <form onSubmit={handleSubmit} className="space-y-5 bg-card border border-border border-b-4 border-b-yellow p-6 md:p-8 shadow-sm" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground text-sm">Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Dein Name"
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground text-sm">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="deine@email.at"
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-foreground text-sm">Telefon</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+43 681 000000"
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-foreground text-sm">Firma</Label>
                  <Input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Deine Firma"
                    className="bg-background border-border text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-foreground text-sm">Budget</Label>
                  <Select 
                    value={formData.budget} 
                    onValueChange={(value) => setFormData({ ...formData, budget: value })}
                  >
                    <SelectTrigger className="bg-background border-border text-foreground">
                      <SelectValue placeholder="Budget wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {budgetOptions.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="text-foreground hover:bg-accent"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_contact" className="text-foreground text-sm">Bevorzugter Kontakt</Label>
                  <Select 
                    value={formData.preferred_contact} 
                    onValueChange={(value) => setFormData({ ...formData, preferred_contact: value })}
                  >
                    <SelectTrigger className="bg-background border-border text-foreground">
                      <SelectValue placeholder="Kontaktart wählen" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="email" className="text-foreground hover:bg-accent">Email</SelectItem>
                      <SelectItem value="call" className="text-foreground hover:bg-accent">Telefonat</SelectItem>
                      <SelectItem value="whatsapp" className="text-foreground hover:bg-accent">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-foreground text-sm">Projektdetails *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Erzähl uns von deinem Projekt …"
                  className="bg-background border-border text-foreground placeholder:text-muted-foreground min-h-[100px]"
                  required
                  data-testid="contact-message-input"
                />
              </div>

              {/* Cloudflare Turnstile Widget */}
              {turnstileSiteKey && (
                <div className="flex justify-center" data-testid="turnstile-container">
                  <div ref={turnstileRef}></div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full btn-primary group"
                disabled={submitting || (turnstileSiteKey && !turnstileToken)}
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    Nachricht senden
                    <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>

              {/* Datenschutzinformation */}
              <div className="pt-4 border-t border-border space-y-3 text-xs leading-relaxed text-muted-foreground">
                <p className="font-semibold text-foreground uppercase tracking-[0.12em]">
                  Datenschutzinformation
                </p>
                <p>
                  Mit dem Absenden des Kontaktformulars werden die von Ihnen eingegebenen
                  personenbezogenen Daten zum Zweck der Bearbeitung Ihrer Anfrage verarbeitet.
                </p>
                <p>
                  Die Verarbeitung erfolgt gemäß Art. 6 Abs. 1 lit. a DSGVO auf Grundlage
                  Ihrer Einwilligung.
                </p>
                <p>
                  Weitere Informationen zur Verarbeitung Ihrer personenbezogenen Daten sowie zu
                  Ihren Rechten finden Sie in unserer{' '}
                  <Link to="/privacy" className="text-brand underline underline-offset-2 hover:text-foreground transition-colors">
                    Datenschutzerklärung
                  </Link>.
                </p>
                <p>
                  Sofern auf dieser Website Dienste von Google, beispielsweise Google reCAPTCHA,
                  eingesetzt werden, gelten ergänzend die{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2 hover:text-foreground transition-colors">
                    Datenschutzerklärung von Google
                  </a>{' '}sowie die{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2 hover:text-foreground transition-colors">
                    Nutzungsbedingungen von Google
                  </a>.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
